import * as pack from "../../../package.json";
import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Alert, Badge, Tooltip } from "antd";
import { BellAlertIcon, BookOpenIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { Links } from "../types";
import NotificationsPanel from "./notificationPanel";
import UserGuide from "./userGuide";
import ReleaseNotes from "./releaseNotes";
import { ViteMicroApps } from "../common/types";

const remotes = ((JSON.parse(import.meta.env.VITE_MICRO_APPS ?? "[]") ?? []) as ViteMicroApps[]).map((el) => {
    return {
      name: el.title,
      href: el.href
    }
})

const Header = () => {
  const initialLinks: Links[] = [
    { name: "Chat", href: "/" },
    { name: "Workflows", href: "/workflows" },
    { name: "Usage Reporting", href: "/dashboard" },
    { name: "Chain of Custody", href: "/chain-of-custody" },
    ...remotes
  ];
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.userDetails)
  const notifications = useSelector((state: RootState) => state.appInfo.notifications)
  const [links, setLinks] = useState<Links[]>(initialLinks);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showReleaseVersion, setShowReleaseVersion] = useState(false);
  const [highlightUpdate, setHighlightUpdate] = useState<boolean>(false);

  const versionButtonRef = useRef<HTMLDivElement | null>(null);

  const getUnreadNotificationCount = () => {
    return notifications?.filter(notification => !notification.isRead)?.length || 0;
  }
  const badgeCount = getUnreadNotificationCount();

  useEffect(() => {
    let filteredLinks = initialLinks;

    if (!user?.workflows_enabled) {
      filteredLinks = filteredLinks.filter((link) => link.name !== "Workflows");
    }

    if (!user?.dashboard_usage_enabled) {
      filteredLinks = filteredLinks.filter((link) => link.name !== "Usage Reporting");
    }

    if (!user?.chain_of_custody_enabled) {
      filteredLinks = filteredLinks.filter((link) => link.name !== "Chain of Custody");
    }

    setLinks(filteredLinks);

    if (filteredLinks.length === 0) {
      navigate("redirect");
    }

    if (filteredLinks.length > 0) {
      navigate(filteredLinks[0].href);
    }
  }, [user]);

  useEffect(() => {
    if (!highlightUpdate) return;
  
    const timer = setTimeout(() => {
      setHighlightUpdate(false);
    }, 5000);
  
    const handleClick = () => {
      setHighlightUpdate(false);
    };
  
    document.addEventListener("click", handleClick);
  
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClick);
    };
  }, [highlightUpdate]);

  return (
    <>
      { user?.maintenance?.is_enabled ? (
          <Alert
            className="h-[55px] text-[16px] bg-[#dbcb3d]"
            message={
              <div className="flex items-center justify-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5" />
                <p>{user.maintenance.message ? user.maintenance.message : "MAIA is under maintenance. Please check back soon."}</p>
              </div>
            }
            showIcon={false}
            banner
            closable
          />
        ) : null
      }
      <nav className="bg-secondary text-primary">
        <div className="px-5">
          <div className="flex justify-between h-16">
            <div className="flex  lg:px-0 ">
              <div className="flex flex-shrink-0 pt-2">
                <a className="block  " href="/#">
                  <span className="inline-block  absolute">
                    {" "}
                    <div className="inline-block text-accent">
                      <div title="Logo" aria-label="Logo" className="app-logo">
                        <span className="sr-only">
                          {" "}
                          {user?.client_app_config.primary_title === ""
                            ? "MAIA"
                            : user?.client_app_config.primary_title}
                        </span>
                      </div>
                    </div>{" "}
                  </span>
                  <div className="ml-14 flex justify-center flex-col">
                    <span className="text-base">
                      {user?.client_app_config.primary_title === ""
                        ? "MAIA"
                        : user?.client_app_config.primary_title}
                    </span>
                    <span className="text-xs">
                      {user?.client_app_config.secondary_title === ""
                        ? "Multifunctional AI Assistant"
                        : user?.client_app_config.secondary_title}
                    </span>
                  </div>
                </a>
              </div>

              <div className="flex ml-6">
                {links.map((data, index) => {
                  return (
                    <div
                      key={index + "linkrow"}
                      className={`text-primary  items-center hover:text-accent  px-1 pt-1 block   text-sm font-medium `}
                    >
                      <NavLink
                        className="hover:text-accent h-full flex flex-col"
                        to={data.href}
                      >
                        {({ isActive }) => (
                          <>
                            <div className=" flex-1 flex-col flex">
                              <div className="flex-1"></div>
                              <div className="pb-2 px-3">{data.name}</div>
                            </div>
                            <div
                              className={`${isActive ? "bg-accent" : "bg-secondary"
                                } w-full h-1 rounded-t-lg `}
                            ></div>
                          </>
                        )}
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-auto mb-1 flex gap-5 items-center">
              <div className={highlightUpdate ? "pulse-effect" : ""}>
                <div
                  ref={versionButtonRef}
                  className={`relative z-50`}
                >
                  <Tooltip
                    title="Check out the latest changes"
                    open={highlightUpdate}
                    placement="bottom"
                  >
                    <button
                      className={`bg-accent text-white rounded text-xs p-[5px] ${highlightUpdate ? "ring-2 ring-yellow-400" : ""}`}
                      onClick={() => {
                        setShowReleaseVersion(true);
                        setHighlightUpdate(false);
                      }}
                    >
                      Version: {pack.version}
                    </button>
                  </Tooltip>
                </div>
              </div>
              <button
                className="bg-accent text-white rounded text-xs p-[5px]"
                onClick={() => setShowUserGuide(true)}
              >
                <BookOpenIcon className="w-4 h-4 inline-block mr-1" />
                User Guide
              </button>
              <Badge count={badgeCount}>
                <button
                  className="bg-accent text-white rounded"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <BellAlertIcon className="w-5 h-5 m-1" />
                </button>
              </Badge>
            </div>
          </div>
        </div>
      </nav>
      <NotificationsPanel
        open={showNotifications}
        onClose={() => {
          setShowNotifications(false);
        }}
      />
      <UserGuide
        open={showUserGuide}
        onCancel={() => {
          setShowUserGuide(false);
        }}
      />
      <ReleaseNotes
        open={showReleaseVersion}
        onCancel={() => {
          setShowReleaseVersion(false);
        }}
        onHighlight={() => {
          setHighlightUpdate(true);
        }}
      />
    </>
  );
};

export default Header;
